import { Component, OnInit } from '@angular/core';
import { Folder } from '@model/folder';
import { FolderService } from '@service/folder/folder.service';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { EmitterService } from '@service/emitter/emitter.service';
import { DateHelperService } from '@theme/utils/date-helper.service';
import { Globals } from '@theme/utils/globals';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

/**
 * ====================================================================
 * COMPOSANT : APPORT SUPPLÉMENTAIRE SUR DOSSIER
 * ====================================================================
 * 
 * Ce composant permet au client d'ajouter un apport supplémentaire
 * sur son dossier en modalité échéancier.
 * 
 * FONCTIONNALITÉS :
 * 1. Simulation : Calculer l'impact d'un apport sans modifier les données
 * 2. Validation : Enregistrer définitivement l'apport et appliquer le nouveau plan
 * 
 * PROCESSUS UTILISATEUR :
 * 1. Saisir le montant de l'apport
 * 2. Choisir le type de recalcul (réduire durée ou mensualité)
 * 3. Simuler pour voir l'impact
 * 4. Confirmer et enregistrer l'apport
 * 
 * ====================================================================
 */
@Component({
  selector: 'app-folder-apport',
  templateUrl: './folder-apport.component.html',
  styleUrls: ['./folder-apport.component.scss']
})
export class FolderApportComponent implements OnInit {
  
  folder: Folder;
  apportForm: FormGroup;
  simulationResult: any = null;
  isSimulating: boolean = false;
  isSubmitting: boolean = false;
  submit: boolean = false;
  required = Globals.required;

  // Options pour le choix de recalcul
  choixRecalculOptions = [
    { label: 'Réduire la durée (saisir nouvelle durée, mensualité recalculée)', value: 'REDUIRE_DUREE' },
    { label: 'Réduire la mensualité (garder le nombre de mois restant)', value: 'REDUIRE_MENSUALITE' }
  ];

  // Informations actuelles du dossier
  capitalRestantActuel: number = 0;
  echeancesImpayees: any[] = [];
  nombreEcheancesPayees: number = 0;

  constructor(
    public modal: NgbActiveModal,
    private modalService: NgbModal,
    private emitter: EmitterService,
    private folderService: FolderService,
    private formBuild: FormBuilder,
    private toastr: ToastrService
  ) {
    this.folder = this.folderService.getFolder();
    this.initForm();
  }

  ngOnInit(): void {
    this.analyserDossier();
    this.verifierConditionsApport();
  }

  initForm(): void {
    this.apportForm = this.formBuild.group({
      montantApport: [0, [Validators.required, Validators.min(1000)]], // Minimum 1000 F CFA
      choixRecalcul: ['REDUIRE_DUREE', [Validators.required]],
      nouvelleDuree: [null], // Nouvelle durée en mois (requis si REDUIRE_DUREE)
      commentaire: [''] // Commentaire optionnel
    });
    
    // Ajouter une validation dynamique pour nouvelleDuree si REDUIRE_DUREE est sélectionné
    this.apportForm.get('choixRecalcul')?.valueChanges.subscribe(choix => {
      const nouvelleDureeControl = this.apportForm.get('nouvelleDuree');
      if (choix === 'REDUIRE_DUREE') {
        // Attendre que echeancesImpayees soit chargé
        setTimeout(() => {
          const maxDuree = this.echeancesImpayees.length || 999;
          nouvelleDureeControl?.setValidators([Validators.required, Validators.min(1), Validators.max(maxDuree)]);
          nouvelleDureeControl?.updateValueAndValidity();
        }, 100);
      } else {
        nouvelleDureeControl?.clearValidators();
        nouvelleDureeControl?.setValue(null);
        nouvelleDureeControl?.updateValueAndValidity();
      }
    });
  }

  /**
   * Analyser l'état actuel du dossier
   */
  analyserDossier(): void {
    if (!this.folder || !this.folder.echeances) {
      this.toast('Erreur : données du dossier incomplètes', 'Erreur', 'error');
      return;
    }

    // Calculer les échéances payées et impayées/en attente
    this.echeancesImpayees = this.folder.echeances.filter(e => e.etat === 'IMPAYE' || e.etat === 'ATTENTE');
    this.nombreEcheancesPayees = this.folder.echeances.filter(e => e.etat === 'PAYE').length;

    // Le capital restant actuel = capitalRestant de la DERNIÈRE échéance PAYÉE
    // Car il représente ce que le client doit encore après son dernier paiement
    const echeancesPayees = this.folder.echeances
      .filter(e => e.etat === 'PAYE')
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    if (echeancesPayees.length > 0) {
      this.capitalRestantActuel = echeancesPayees[0].capitalRestant;
    } else {
      // Aucune échéance payée : capital restant = montant total du dossier
      this.capitalRestantActuel = this.folder.montant || 0;
    }

    // Mettre à jour la validation du champ nouvelleDuree avec le nombre d'échéances
    if (this.apportForm) {
      const nouvelleDureeControl = this.apportForm.get('nouvelleDuree');
      const choixRecalcul = this.apportForm.get('choixRecalcul')?.value;
      
      if (choixRecalcul === 'REDUIRE_DUREE' && nouvelleDureeControl) {
        const maxDuree = this.echeancesImpayees.length || 999;
        nouvelleDureeControl.setValidators([Validators.required, Validators.min(1), Validators.max(maxDuree)]);
        nouvelleDureeControl.updateValueAndValidity();
      }
    }

    console.log('Analyse du dossier:', {
      totalEcheances: this.folder.echeances.length,
      echeancesPayees: this.nombreEcheancesPayees,
      echeancesImpayees: this.echeancesImpayees.length,
      capitalRestant: this.capitalRestantActuel
    });
  }

  /**
   * Vérifier si les conditions sont réunies pour ajouter un apport
   */
  verifierConditionsApport(): void {
    // Vérifier les échéances partiellement payées
    const echeancesPartielles = this.folder.echeances.filter((e:any) => 
      e.montantPaye > 0 && e.montantPaye < e.montant
    );

    if (echeancesPartielles.length > 0) {
      this.toast(
        'Impossible d\'ajouter un apport : des échéances sont partiellement payées.',
        'Conditions non réunies',
        'warning'
      );
      this.modal.dismiss('conditions-non-reunies');
      return;
    }

    // Vérifier les paiements en attente
    const paymentsEnAttente = this.folder.invoice?.payments?.filter(p => p.etat === 'ATTENTE') || [];
    
    if (paymentsEnAttente.length > 0) {
      this.toast(
        'Impossible d\'ajouter un apport : des paiements sont en attente de validation.',
        'Conditions non réunies',
        'warning'
      );
      this.modal.dismiss('paiements-en-attente');
      return;
    }

    // Vérifier qu'il y a des échéances impayées
    if (this.echeancesImpayees.length === 0) {
      this.toast(
        'Aucune échéance impayée : ajout d\'apport impossible.',
        'Conditions non réunies',
        'info'
      );
      this.modal.dismiss('aucune-echeance-impayee');
      return;
    }
  }

  /**
   * ====================================================================
   * SIMULATION DE L'IMPACT DE L'APPORT
   * ====================================================================
   * 
   * Calcule l'impact d'un apport sans modifier les données du dossier.
   * Permet à l'utilisateur de voir les conséquences avant de valider.
   * 
   * PROCESSUS :
   * 1. Valider le formulaire
   * 2. Vérifier que l'apport est inférieur au capital restant
   * 3. Appeler l'API pour calculer l'impact
   * 4. Afficher les résultats de simulation
   * 
   * ====================================================================
   */
  simulerApport(): void {
    this.submit = true;
    
    if (this.apportForm.invalid) {
      this.toast('Veuillez corriger les erreurs du formulaire.', 'Formulaire invalide', 'warning');
      return;
    }

    const montantApport = this.f.montantApport.value;
    const choixRecalcul = this.f.choixRecalcul.value;
    const nouvelleDuree = this.f.nouvelleDuree.value;

    // Vérifier que l'apport n'est pas supérieur au capital restant
    if (montantApport >= this.capitalRestantActuel) {
      this.toast(
        `L'apport ne peut pas être supérieur ou égal au capital restant (${this.capitalRestantActuel.toLocaleString()} F CFA).`,
        'Montant invalide',
        'warning'
      );
      return;
    }

    // Vérifier que la nouvelle durée est fournie si REDUIRE_DUREE est sélectionné
    if (choixRecalcul === 'REDUIRE_DUREE' && (!nouvelleDuree || nouvelleDuree < 1 || nouvelleDuree > this.echeancesImpayees.length)) {
      this.toast(
        `La nouvelle durée doit être entre 1 et ${this.echeancesImpayees.length} mois.`,
        'Durée invalide',
        'warning'
      );
      return;
    }

    this.isSimulating = true;

    const simulationData: any = {
      montantApport: montantApport,
      choixRecalcul: choixRecalcul,
      folderUuid: this.folder.uuid
    };
    
    // Ajouter nouvelleDuree seulement si REDUIRE_DUREE est sélectionné
    if (choixRecalcul === 'REDUIRE_DUREE') {
      simulationData.nouvelleDuree = nouvelleDuree;
    }

    this.folderService.simulerApportSupplementaire(simulationData).subscribe({
      next: (result) => {
        this.simulationResult = result.data;
        this.isSimulating = false;
        
        this.toast(
          'Simulation réussie ! Consultez les résultats ci-dessous.',
          'Simulation terminée',
          'success'
        );
      },
      error: (error) => {
        this.isSimulating = false;
        console.error('Erreur simulation:', error);
        
        const message = error.error?.message || 'Erreur lors de la simulation';
        this.toast(message, 'Erreur de simulation', 'error');
      }
    });
  }

  /**
   * Confirmer et enregistrer l'apport
   */
  confirmerApport(): void {
    if (!this.simulationResult) {
      this.toast('Veuillez d\'abord effectuer une simulation.', 'Simulation requise', 'warning');
      return;
    }

    Swal.fire({
      title: 'Confirmer l\'apport supplémentaire',
      html: this.genererRecapitulatifHtml(),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmer l\'apport',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#dc3545'
    }).then((result) => {
      if (result.isConfirmed) {
        this.enregistrerApport();
      }
    });
  }

  /**
   * ====================================================================
   * ENREGISTREMENT DÉFINITIF DE L'APPORT
   * ====================================================================
   * 
   * Enregistre l'apport et applique le nouveau plan d'amortissement.
   * Cette action modifie définitivement les échéances du dossier.
   * 
   * PROCESSUS :
   * 1. Préparer les données de l'apport
   * 2. Appeler l'API pour enregistrer l'apport
   * 3. Appliquer le nouveau plan d'amortissement (modification des échéances)
   * 4. Émettre un événement pour rafraîchir l'affichage
   * 5. Fermer le modal
   * 
   * ⚠️ ATTENTION : Cette action est irréversible (sauf annulation manuelle)
   * 
   * ====================================================================
   */
  enregistrerApport(): void {
    this.isSubmitting = true;

    const apportData: any = {
      montantApport: this.f.montantApport.value,
      choixRecalcul: this.f.choixRecalcul.value,
      commentaire: this.f.commentaire.value,
      folderUuid: this.folder.uuid,
      simulationData: this.simulationResult // Inclure les données de simulation
    };
    
    // Ajouter nouvelleDuree seulement si REDUIRE_DUREE est sélectionné
    if (this.f.choixRecalcul.value === 'REDUIRE_DUREE') {
      apportData.nouvelleDuree = this.f.nouvelleDuree.value;
    }

    this.folderService.ajouterApportSupplementaire(apportData).subscribe({
      next: (result) => {
        this.isSubmitting = false;
        
        this.toast(
          'Apport supplémentaire enregistré avec succès !',
          'Apport enregistré',
          'success'
        );

        // Émettre un événement pour rafraîchir la liste
        this.emitter.emit({
          action: 'APPORT_AJOUTE',
          payload: result
        });

        // Fermer le modal
        this.modal.close(result);
      },
      error: (error) => {
        this.isSubmitting = false;
        console.error('Erreur enregistrement:', error);
        
        const message = error.error?.message || 'Erreur lors de l\'enregistrement';
        this.toast(message, 'Erreur d\'enregistrement', 'error');
      }
    });
  }

  /**
   * Générer le récapitulatif HTML pour la confirmation
   */
  genererRecapitulatifHtml(): string {
    if (!this.simulationResult) return '';

    const choixLabel = this.choixRecalculOptions.find(
      option => option.value === this.f.choixRecalcul.value
    )?.label || '';

    return `
      <div class="text-left">
        <h6 class="text-primary">Récapitulatif de l'apport</h6>
        <hr>
        <p><strong>Montant de l'apport :</strong> ${this.f.montantApport.value.toLocaleString()} F CFA</p>
        <p><strong>Choix :</strong> ${choixLabel}</p>
        <p><strong>Capital restant actuel :</strong> ${this.simulationResult.capitalRestantAvant.toLocaleString()} F CFA</p>
        <p><strong>Nouveau capital restant :</strong> ${this.simulationResult.capitalRestantApres.toLocaleString()} F CFA</p>
        <hr>
        <h6 class="text-success">Impact prévu :</h6>
        <p><strong>Ancienne mensualité :</strong> ${this.simulationResult.ancienneMensualite.toLocaleString()} F CFA</p>
        <p><strong>Nouvelle mensualité :</strong> ${this.simulationResult.nouvelleMensualite.toLocaleString()} F CFA</p>
        <p><strong>Ancienne durée restante :</strong> ${this.simulationResult.ancienneDuree} mois</p>
        <p><strong>Nouvelle durée restante :</strong> ${this.simulationResult.nouvelleDuree} mois</p>
        ${this.simulationResult.economieTotal ? 
          `<p class="text-success"><strong>Économie totale :</strong> ${this.simulationResult.economieTotal.toLocaleString()} F CFA</p>` 
          : ''
        }
      </div>
    `;
  }

  /**
   * Fermer le modal
   */
  onClose(): void {
    this.modal.dismiss('ferme');
  }

  /**
   * Réinitialiser la simulation
   */
  reinitialiserSimulation(): void {
    this.simulationResult = null;
    this.submit = false;
  }

  /**
   * Afficher un toast
   */
  toast(msg: string, title: string, type: 'info' | 'success' | 'warning' | 'error'): void {
    switch (type) {
      case 'info':
        this.toastr.info(msg, title);
        break;
      case 'success':
        this.toastr.success(msg, title);
        break;
      case 'warning':
        this.toastr.warning(msg, title);
        break;
      case 'error':
        this.toastr.error(msg, title);
        break;
    }
  }

  // Getters pour faciliter l'accès aux contrôles du formulaire
  get f() { 
    return this.apportForm.controls; 
  }

  get capitalRestantApresApport(): number {
    const montantApport = this.f.montantApport.value || 0;
    return this.capitalRestantActuel - montantApport;
  }

  get montantApportValide(): boolean {
    const montant = this.f.montantApport.value || 0;
    return montant > 0 && montant < this.capitalRestantActuel;
  }
}